import React, { useState, useEffect } from 'react';
import './PlatformSelection.scss';

interface FirmDetails {
  recommended_objective?: string;
  recommended_tone?: string;
  recommended_target_audience?: string[];
}

interface PlatformSelectionProps {
  firmDetails: FirmDetails;
  onSubmit: (formData: any) => void;
  isLoading?: boolean;
}

interface FormData {
  ad_platform: string[];
  ad_objective: string;
  language_and_tone: string;
  target_audience: string[];
  ad_motive: string;
  ad_language: string;
  offers_promotions: string;
}

const PlatformSelection: React.FC<PlatformSelectionProps> = ({ 
  firmDetails, 
  onSubmit, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState<FormData>({
    ad_platform: [],
    ad_objective: firmDetails.recommended_objective || 'Brand awareness',
    language_and_tone: firmDetails.recommended_tone || 'Professional and engaging',
    target_audience: firmDetails.recommended_target_audience || [],
    ad_motive: '',
    ad_language: 'English',
    offers_promotions: ''
  });

  const [languages, setLanguages] = useState<any[]>([]);
  const [objectives, setObjectives] = useState<string[]>([]);
  const [tones, setTones] = useState<string[]>([]);

  const platforms = [
    { id: 'Facebook', name: 'Facebook', icon: 'bi-facebook' },
    { id: 'Instagram', name: 'Instagram', icon: 'bi-instagram' },
    { id: 'LinkedIn', name: 'LinkedIn', icon: 'bi-linkedin' },
    { id: 'Twitter', name: 'X (Twitter)', icon: 'bi-twitter-x' },
  ];

  useEffect(() => {
    fetchConfigData();
  }, []);

  const fetchConfigData = async () => {
    try {
      const apiUrlBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const [langsRes, objRes, tonesRes] = await Promise.all([
        fetch(`${apiUrlBase}v2/config/languages`),
        fetch(`${apiUrlBase}v2/config/objectives`),
        fetch(`${apiUrlBase}v2/config/tones`)
      ]);

      const langsData = await langsRes.json();
      const objData = await objRes.json();
      const tonesData = await tonesRes.json();

      setLanguages(langsData.data?.values || []);
      setObjectives(objData.data?.values || []);
      setTones(tonesData.data?.values || []);
    } catch (error) {
      console.error('Error fetching config data:', error);
    }
  };

  const togglePlatform = (platformId: string) => {
    setFormData(prev => ({
      ...prev,
      ad_platform: prev.ad_platform.includes(platformId)
        ? prev.ad_platform.filter(p => p !== platformId)
        : [...prev.ad_platform, platformId]
    }));
  };

  const handleTargetAudienceChange = (value: string) => {
    const audiences = value.split(',').map(a => a.trim()).filter(a => a);
    setFormData(prev => ({ ...prev, target_audience: audiences }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.ad_platform.length === 0) {
      alert('Please select at least one platform');
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <div className="platform-selection">
      <h2 className="platform-title">Configure Your Advertisement</h2>
      
      <form onSubmit={handleSubmit} className="ad-config-form">
        {/* Platform Selection */}
        <div className="form-section">
          <label className="form-label">Select Platform(s) *</label>
          <div className="platform-grid">
            {platforms.map((platform) => (
              <button
                key={platform.id}
                type="button"
                className={`platform-card ${formData.ad_platform.includes(platform.id) ? 'selected' : ''}`}
                onClick={() => togglePlatform(platform.id)}
              >
                <i className={platform.icon}></i>
                <span>{platform.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Primary Objective */}
        <div className="form-section">
          <label className="form-label">Primary Objective *</label>
          <select
            className="form-select"
            value={formData.ad_objective}
            onChange={(e) => setFormData(prev => ({ ...prev, ad_objective: e.target.value }))}
            required
          >
            {objectives.map(obj => (
              <option key={obj} value={obj}>{obj}</option>
            ))}
          </select>
          <small className="form-hint">AI recommended based on your business</small>
        </div>

        {/* Advertisement Tone */}
        <div className="form-section">
          <label className="form-label">Advertisement Tone *</label>
          <select
            className="form-select"
            value={formData.language_and_tone}
            onChange={(e) => setFormData(prev => ({ ...prev, language_and_tone: e.target.value }))}
            required
          >
            {tones.map(tone => (
              <option key={tone} value={tone}>{tone}</option>
            ))}
          </select>
          <small className="form-hint">AI recommended based on your brand</small>
        </div>

        {/* Target Audience */}
        <div className="form-section">
          <label className="form-label">Target Audience *</label>
          <input
            type="text"
            className="form-input"
            value={formData.target_audience.join(', ')}
            onChange={(e) => handleTargetAudienceChange(e.target.value)}
            placeholder="e.g., Young professionals, Parents, Tech enthusiasts"
            required
          />
          <small className="form-hint">AI recommended audience segments (comma-separated)</small>
        </div>

        {/* Advertisement Motive */}
        <div className="form-section">
          <label className="form-label">Advertisement Motive (Optional)</label>
          <textarea
            className="form-textarea"
            value={formData.ad_motive}
            onChange={(e) => setFormData(prev => ({ ...prev, ad_motive: e.target.value }))}
            placeholder="Describe your specific goal for this advertisement..."
            rows={3}
          />
        </div>

        {/* Language Selection */}
        <div className="form-section">
          <label className="form-label">Advertisement Language *</label>
          <select
            className="form-select"
            value={formData.ad_language}
            onChange={(e) => setFormData(prev => ({ ...prev, ad_language: e.target.value }))}
            required
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.name}>{lang.name}</option>
            ))}
          </select>
        </div>

        {/* Offers/Promotions */}
        <div className="form-section">
          <label className="form-label">Offers or Promotions (Optional)</label>
          <input
            type="text"
            className="form-input"
            value={formData.offers_promotions}
            onChange={(e) => setFormData(prev => ({ ...prev, offers_promotions: e.target.value }))}
            placeholder="e.g., 20% off, Free shipping, Buy one get one"
          />
        </div>

        <button 
          type="submit" 
          className="submit-btn"
          disabled={isLoading}
        >
          {isLoading ? 'Generating...' : 'Generate Advertisement'}
        </button>
      </form>
    </div>
  );
};

export default PlatformSelection;
